'use client';

import React, { memo } from 'react';
import { useRouter } from 'next/navigation';
import { useTopGainers } from '../../hooks/useTopGainers'; 

// ==========================================
// 1. STYLES & COLORS (Đồng bộ với Dashboard)
// ==========================================
const colors = {
  bg: '#020617', 
  cardBg: '#0f172a', 
  border: '#1e293b',
  textMain: '#f8fafc', 
  textSub: '#94a3b8',
  green: '#22c55e', 
  greenBg: 'rgba(34, 197, 94, 0.1)',
  red: '#ef4444', 
  redBg: 'rgba(239, 68, 68, 0.1)',
  hover: 'rgba(255, 255, 255, 0.05)'
};

const styles = {
  card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)' },
  flexBetween: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  // [CẬP NHẬT]: Điều chỉnh độ rộng cột số 2 để có đủ chỗ chứa Nút Chart
  gridHeader: { display: 'grid', gridTemplateColumns: '40px 1.8fr 1.5fr 1.2fr 1.5fr', gap: '12px', alignItems: 'center', color: colors.textSub, fontSize: '12px', textTransform: 'uppercase', paddingBottom: '12px', borderBottom: `1px solid ${colors.border}`, marginBottom: '12px' },
  gridRow: { display: 'grid', gridTemplateColumns: '40px 1.8fr 1.5fr 1.2fr 1.5fr', gap: '12px', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '8px', borderLeft: `3px solid transparent`, transition: 'all 0.2s' },
  coinBadge: { cursor: 'pointer', fontWeight: 'bold', color: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.1)', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' },
  refreshBtn: { backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textMain, padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }
};

// ==========================================
// 2. COMPONENT TOP GAINERS (ĐÃ BỌC MEMO)
// ==========================================
const TopGainers = memo(function TopGainers({ onSelectCoin }) {
  const router = useRouter();
  const { gainers, loading, error, lastUpdate, refresh } = useTopGainers(20);

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '--';
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null) return '--';
    if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(6)}`;
  };

  const handleSelectCoin = (symbol) => {
    if (onSelectCoin) {
      onSelectCoin(symbol);
    } else {
      router.push(`/coin/${symbol}`);
    }
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <div style={{ color: colors.textMain, fontWeight: 'bold', fontSize: '16px' }}>🚀 TOP 20 GAINERS (FUTURES)</div>
        <p style={{ textAlign: 'center', padding: '40px 0', color: colors.textSub, fontSize: '14px' }}>⏳ Đang quét dữ liệu thị trường...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.card}>
        <div style={{ color: colors.textMain, fontWeight: 'bold', fontSize: '16px' }}>🚀 TOP 20 GAINERS (FUTURES)</div>
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <p style={{ color: colors.red, marginBottom: '16px' }}>❌ Lỗi kết nối: {error}</p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button onClick={refresh} style={styles.refreshBtn}>🔄 Thử lại ngay</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      {/* HEADER */}
      <div style={{ ...styles.flexBetween, marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, color: colors.textMain, fontSize: '16px', fontWeight: 'bold' }}>🚀 TOP 20 GAINERS (FUTURES)</h3>
          <p style={{ fontSize: '12px', color: colors.textSub, margin: '4px 0 0 0' }}>🔥 20 coin có sức bật mạnh nhất 24h qua (Vol &gt; 1M)</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '11px', color: colors.textSub }}>
            {lastUpdate && `⏱ Cập nhật: ${lastUpdate.toLocaleTimeString('vi-VN')}`}
          </span>
          <button onClick={refresh} style={styles.refreshBtn}>
            🔄 LÀM MỚI
          </button>
        </div>
      </div>
      
      {/* DATA GRID */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '550px' }}> {/* Tăng minWidth xíu cho khỏi vỡ UI */}
          
          {/* Grid Titles */}
          <div style={styles.gridHeader}>
            <div>#</div>
            <div>COIN</div>
            <div>PRICE (LAST)</div>
            <div>24H CHANGE</div>
            <div style={{ textAlign: 'right' }}>24H VOLUME</div>
          </div>

          {/* Grid Rows */}
          {gainers.length > 0 ? (
            gainers.map((coin, index) => {
              const isUp = coin.change24h >= 0;
              return (
                <div 
                  key={coin.symbol} 
                  style={{
                    ...styles.gridRow,
                    borderLeftColor: isUp ? colors.green : colors.red 
                  }}
                >
                  <div style={{ color: colors.textSub, fontWeight: 'bold' }}>{index + 1}</div>
                  
                  {/* [CẬP NHẬT]: Khu vực hiển thị Tên Coin + Nút Chart */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span 
                      onClick={() => handleSelectCoin(coin.symbol)}
                      style={styles.coinBadge}
                      title={`Xem chi tiết trên Dashboard`}
                    >
                      {coin.symbol}
                    </span>
                    
                    {/* Nút bấm mở Chart Binance */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); 
                        window.open(`https://www.binance.com/en/futures/${coin.symbol}`, '_blank');
                      }}
                      style={{
                        backgroundColor: 'rgba(59, 130, 246, 0.05)',
                        border: '1px solid #3b82f6',
                        color: '#3b82f6',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s',
                        outline: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#3b82f6';
                        e.currentTarget.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                        e.currentTarget.style.color = '#3b82f6';
                      }}
                      title="Mở biểu đồ Binance"
                    >
                      📈 CHART
                    </button>
                  </div>

                  <div style={{ color: colors.textMain, fontWeight: '500' }}>
                    {formatPrice(coin.price)}
                  </div>
                  <div style={{ color: isUp ? colors.green : colors.red, fontWeight: 'bold' }}>
                    {isUp ? '▲' : '▼'} {Math.abs(coin.change24h).toFixed(2)}%
                  </div>
                  <div style={{ color: colors.textSub, textAlign: 'right' }}>
                    {formatNumber(coin.volume24h)}
                  </div>
                </div>
              );
            })
          ) : (
             <p style={{ textAlign: 'center', padding: '20px', color: colors.textSub }}>Không có dữ liệu phù hợp.</p>
          )}

        </div>
      </div>
    </div>
  );
});

export default TopGainers;
